import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

import {
  Button,
  Page,
  PageBody,
  PageHeader,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui";
import {
  useApproveCloseCalendarItem,
  useCloseCalendarActiveTemplate,
  useCloseCalendarCurrentPeriod,
  useDeleteCloseCalendarRule,
  useUpdateCloseCalendarRule,
} from "./api";
import { ClosePeriodStatus, PipelineView } from "./PipelineView";
import { RulesEngineView } from "./RulesEngineView";

// Re-exported for tests
export {
  getCloseCalendarStatusVariant,
  getCloseCalendarCompletionSummary,
} from "./utils";

function ErrorMessage({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : "Request failed";
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
      {message}
    </div>
  );
}

export function CloseCalendarPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const periodQuery = useCloseCalendarCurrentPeriod();
  const templateQuery = useCloseCalendarActiveTemplate();
  const approveItem = useApproveCloseCalendarItem();
  const updateRule = useUpdateCloseCalendarRule();
  const deleteRule = useDeleteCloseCalendarRule();
  const activeTab = location.pathname.endsWith("/rules") ? "rules" : "calendar";
  const periodItems = useMemo(
    () => periodQuery.data?.items ?? [],
    [periodQuery.data],
  );

  return (
    <Page maxWidth="xl">
      <PageHeader
        title="Close Calendar"
        description="Track the month-end close and approve each step in sequence."
        actions={
          <Button
            variant="secondary"
            onClick={() => periodQuery.refetch()}
            disabled={periodQuery.isFetching}
          >
            <ArrowPathIcon className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        }
      />
      <PageBody>
        <Tabs
          defaultValue="calendar"
          value={activeTab}
          onValueChange={(value) =>
            navigate(
              value === "rules" ? "/close-calendar/rules" : "/close-calendar",
            )
          }
        >
          <TabsList>
            <TabsTrigger value="calendar">Pipeline</TabsTrigger>
            <TabsTrigger value="rules">Rules Engine</TabsTrigger>
          </TabsList>

          <TabsContent value="calendar" className="space-y-4">
            {periodQuery.isLoading && (
              <div className="flex min-h-[240px] items-center justify-center">
                <Spinner />
              </div>
            )}
            {periodQuery.error && <ErrorMessage error={periodQuery.error} />}
            {periodQuery.data && (
              <>
                <ClosePeriodStatus period={periodQuery.data} />
                <PipelineView
                  items={periodItems}
                  isApproving={approveItem.isPending}
                  onApprove={(itemId) => approveItem.mutate(itemId)}
                />
              </>
            )}
          </TabsContent>

          <TabsContent value="rules" className="space-y-4">
            {templateQuery.isLoading && (
              <div className="flex min-h-[240px] items-center justify-center">
                <Spinner />
              </div>
            )}
            {templateQuery.error && (
              <ErrorMessage error={templateQuery.error} />
            )}
            {updateRule.error && <ErrorMessage error={updateRule.error} />}
            {deleteRule.error && <ErrorMessage error={deleteRule.error} />}
            {templateQuery.data && (
              <RulesEngineView
                template={templateQuery.data}
                period={periodQuery.data}
                isSaving={updateRule.isPending}
                onSaveRule={(ruleId, changes) =>
                  updateRule.mutate({ ruleId, ...changes })
                }
                isDeleting={deleteRule.isPending}
                onDeleteRule={(ruleIds) =>
                  Promise.all(ruleIds.map(id => deleteRule.mutateAsync(id)))
                }
              />
            )}
          </TabsContent>
        </Tabs>
      </PageBody>
    </Page>
  );
}
